import { useParams, useNavigate } from 'react-router-dom';
import { AppLayout } from '../components/layout';
import { Card, Button, Badge, Table } from '../components/ui';
import type { Column } from '../components/ui/Table';
import useProvidersStore from '../stores/providersStore';

interface Appointment {
	date: string;
	customer: string;
	service: string;
	revenue: number;
	status: string;
}

// Mock appointments data
const mockAppointments: Record<string, Appointment[]> = {
	'keisha-b': [
		{ date: '2025-10-15', customer: 'Sarah Johnson', service: 'Tribal Braids', revenue: 280, status: 'Completed' },
		{ date: '2025-10-14', customer: 'Michelle Davis', service: 'Tribal Braids', revenue: 280, status: 'Completed' },
		{ date: '2025-10-12', customer: 'Amanda Wilson', service: 'Box Braids', revenue: 220, status: 'Completed' },
		{ date: '2025-10-10', customer: 'Jessica Brown', service: 'Tribal Braids', revenue: 280, status: 'Completed' },
		{ date: '2025-10-08', customer: 'Lisa Martinez', service: 'Knotless Medium', revenue: 250, status: 'Completed' },
	],
	'musu-k': [
		{ date: '2025-10-16', customer: 'Maria Lopez', service: 'Knotless Medium', revenue: 250, status: 'Completed' },
		{ date: '2025-10-14', customer: 'Ashley Taylor', service: 'Knotless Large', revenue: 230, status: 'Completed' },
		{ date: '2025-10-11', customer: 'Rachel Green', service: 'Knotless Medium', revenue: 250, status: 'Completed' },
		{ date: '2025-10-09', customer: 'Emily White', service: 'Knotless Small', revenue: 280, status: 'Completed' },
		{ date: '2025-10-07', customer: 'Nicole Harris', service: 'Knotless Medium', revenue: 250, status: 'Completed' },
	],
	'rita-s': [
		{ date: '2025-10-15', customer: 'Tiffany Lee', service: 'Box Braids', revenue: 240, status: 'Completed' },
		{ date: '2025-10-13', customer: 'Vanessa King', service: 'Box Braids Medium', revenue: 250, status: 'Completed' },
		{ date: '2025-10-10', customer: 'Crystal Moore', service: 'Box Braids', revenue: 240, status: 'Completed' },
		{ date: '2025-10-08', customer: 'Diana Clark', service: 'Box Braids Large', revenue: 220, status: 'Completed' },
		{ date: '2025-10-05', customer: 'Monica Lewis', service: 'Box Braids', revenue: 240, status: 'Completed' },
	],
	'asha-n': [
		{ date: '2025-10-16', customer: 'Jasmine Walker', service: 'Natural Style', revenue: 170, status: 'Completed' },
		{ date: '2025-10-14', customer: 'Maya Robinson', service: 'Twist Out', revenue: 150, status: 'Completed' },
		{ date: '2025-10-11', customer: 'Zoe Turner', service: 'Natural Style', revenue: 170, status: 'Completed' },
		{ date: '2025-10-09', customer: 'Alicia Scott', service: 'Protective Style', revenue: 180, status: 'Completed' },
		{ date: '2025-10-06', customer: 'Carmen Hill', service: 'Natural Style', revenue: 170, status: 'Completed' },
	],
};

const ProviderDetail = () => {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const providers = useProvidersStore(state => state.providers);

	const provider = providers.find(p => p.id === id);
	const appointments = mockAppointments[id || ''] || [];

	if (!provider) {
		return (
			<AppLayout>
				<Card>
					<div className="text-center py-12">
						<h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">Provider Not Found</h2>
						<Button variant="primary" onClick={() => navigate('/providers')}>
							Back to Providers
						</Button>
					</div>
				</Card>
			</AppLayout>
		);
	}

	const columns: Column<Appointment>[] = [
		{
			key: 'date',
			header: 'Date',
			sortable: true,
			render: value => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
		},
		{
			key: 'customer',
			header: 'Customer',
			sortable: true,
		},
		{
			key: 'service',
			header: 'Service',
			sortable: true,
		},
		{
			key: 'revenue',
			header: 'Revenue',
			sortable: true,
			render: value => <span className="font-semibold text-green-600 dark:text-green-400">${value.toLocaleString()}</span>,
		},
		{
			key: 'status',
			header: 'Status',
			render: value => <Badge variant="success">{value}</Badge>,
		},
	];

	return (
		<AppLayout>
			{/* Back Link */}
			<div
				className="flex items-center gap-2 text-blue-600 dark:text-blue-400 cursor-pointer hover:underline mb-4 text-sm font-medium"
				onClick={() => navigate('/providers')}
			>
				<svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
					<path
						fillRule="evenodd"
						d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
					/>
				</svg>
				Back to Providers
			</div>

			{/* Provider Info Card */}
			<Card className="mb-5">
				<div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-6">
					<div className="flex-1 min-w-0">
						<h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2 truncate">{provider.name}</h1>
						<p className="text-base md:text-lg text-gray-600 dark:text-gray-400 mb-1">{provider.specialty}</p>
						<p className="text-sm text-gray-500 dark:text-gray-500">
							{provider.city} • Joined Jan 2024
						</p>
					</div>
					<Button variant="primary" onClick={() => navigate(`/providers/${id}/edit`)} className="w-full sm:w-auto flex-shrink-0">
						Edit Profile
					</Button>
				</div>

				{/* Info Grid */}
				<div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4">
					<div className="bg-white dark:bg-gray-700 p-3 md:p-4 rounded-lg transition-colors">
						<div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Phone</div>
						<div className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">(555) 123-4567</div>
					</div>
					<div className="bg-white dark:bg-gray-700 p-3 md:p-4 rounded-lg transition-colors">
						<div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Email</div>
						<div className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{provider.name.toLowerCase().replace(' ', '')}@example.com</div>
					</div>
					<div className="bg-white dark:bg-gray-700 p-3 md:p-4 rounded-lg transition-colors">
						<div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Total Appointments</div>
						<div className="text-sm font-semibold text-gray-900 dark:text-gray-100">{provider.appointments}</div>
					</div>
					<div className="bg-white dark:bg-gray-700 p-3 md:p-4 rounded-lg transition-colors">
						<div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Total Revenue</div>
						<div className="text-sm font-semibold text-green-600 dark:text-green-400">${provider.revenue.toLocaleString()}</div>
					</div>
					<div className="bg-white dark:bg-gray-700 p-3 md:p-4 rounded-lg transition-colors">
						<div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Avg Rating</div>
						<div className="text-sm font-semibold text-gray-900 dark:text-gray-100">4.9 ⭐</div>
					</div>
				</div>
			</Card>

			{/* Appointments History */}
			<Card>
				<h2 className="text-lg md:text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">Appointment History</h2>
				<div className="overflow-x-auto -mx-[18px] px-[18px]">
					<Table data={appointments} columns={columns} emptyMessage="No appointments found" />
				</div>
			</Card>

			<div className="mt-6 text-sm text-gray-500 dark:text-gray-400">© Beauty Business Portal</div>
		</AppLayout>
	);
};

export default ProviderDetail;
